// ImplantCompanies.js - Same UI as Brands/Variations | City removed | Pagination exactly like ProductListUK

import React, { useState, useEffect } from 'react';
import { FaSearch, FaPlus, FaTrash, FaEdit, FaCheckDouble, FaEllipsisV, FaBuilding, FaChevronRight } from 'react-icons/fa';
import { FaChartSimple } from "react-icons/fa6";
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import TablePagination from '../../components/TablePagination';
import './Implantcompany.css';

const CACHE_PREFIX = 'implantcompanies_cache_';
const PAGE_SIZE = 10; // ⭐ Har page par sirf itni rows

const getCachedCompanies = (shopId) => {
  try {
    const cached = localStorage.getItem(`${CACHE_PREFIX}${shopId}`);
    if (cached) return JSON.parse(cached);
  } catch (e) {}
  return null;
};

const setCachedCompanies = (shopId, data) => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}${shopId}`, JSON.stringify(data));
  } catch (e) {}
};

const clearCompaniesCache = (shopId) => {
  try {
    localStorage.removeItem(`${CACHE_PREFIX}${shopId}`);
  } catch (e) {}
};

const ImplantCompanies = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showActionDropdown, setShowActionDropdown] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [shopName, setShopName] = useState('');
  const [selectedShopId, setSelectedShopId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [shops, setShops] = useState([]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentShopId, setCurrentShopId] = useState(null);
  const [loadedShopId, setLoadedShopId] = useState(null);

  // ⭐ Pagination state
  const [page, setPage] = useState(1);

  // Move To (copy to other shops)
  const [showMoveList, setShowMoveList] = useState(false);
  const [moveShopIds, setMoveShopIds] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

  const getShopIdFromSources = () => {
    const match = location.pathname.match(/\/shop\/(\d+)/);
    if (match) return match[1];
    return localStorage.getItem('selectedShopId')
        || localStorage.getItem('selected_shop_id')
        || null;
  };

  const showSuccessAlert = (message) => {
    Swal.fire({ icon: 'success', title: 'Success!', text: message, timer: 2000, showConfirmButton: true, confirmButtonColor: '#3085d6' });
  };

  const showErrorAlert = (message) => {
    Swal.fire({ icon: 'error', title: 'Error!', text: message, confirmButtonColor: '#d33' });
  };

  const showWarningAlert = (message) => {
    Swal.fire({ icon: 'warning', title: 'Warning!', text: message, confirmButtonColor: '#f0ad4e' });
  };

  const showDeleteConfirmation = (companyName, isMultiple = false, count = 1) => {
    const title = isMultiple
      ? `Are you sure you want to delete ${count} company/companies?`
      : `Are you sure you want to delete company "${companyName}"?`;
    return Swal.fire({
      title: 'Are you sure?', text: title, icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6', confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
  };

  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/shops/`);
      if (response.ok) {
        const data = await response.json();
        const shopsData = Array.isArray(data) ? data : [];
        setShops(shopsData);
        return shopsData;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  const fetchCompanies = async (targetShopId) => {
    if (!targetShopId) return;

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/implant-companies/?shop_id=${targetShopId}`);

      if (response.ok) {
        const data = await response.json();
        const companiesData = Array.isArray(data) ? data : [];
        setCompanies(companiesData);
        setCachedCompanies(targetShopId, companiesData);

        if (companiesData.length > 0 && companiesData[0].shop) {
          setShopName(companiesData[0].shop.shop_name);
        } else {
          try {
            const shopResponse = await fetch(`${API_BASE_URL}/shops/${targetShopId}/`);
            if (shopResponse.ok) {
              const shopData = await shopResponse.json();
              setShopName(shopData.shop_name);
            } else {
              setShopName(`Shop ${targetShopId}`);
            }
          } catch (err) {
            setShopName(`Shop ${targetShopId}`);
          }
        }
      } else {
        setCompanies([]);
      }
    } catch (error) {
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initializeComponent = async () => {
      setInitialLoading(true);
      const shopsData = await fetchShops();

      const shopIdFromSources = getShopIdFromSources();

      if (shopIdFromSources) {
        setCurrentShopId(parseInt(shopIdFromSources));
      } else if (shopsData.length > 0) {
        const firstShopId = shopsData[0].shop_id;
        localStorage.setItem('selectedShopId', firstShopId);
        setCurrentShopId(firstShopId);
        navigate(`/shop/${firstShopId}/implant-companys`, { replace: true });
      }

      setInitialLoading(false);
    };
    initializeComponent();
  }, []);

  useEffect(() => {
    const shopIdFromSources = getShopIdFromSources();
    if (shopIdFromSources && String(shopIdFromSources) !== String(loadedShopId)) {
      const cachedData = getCachedCompanies(shopIdFromSources);
      if (cachedData && cachedData.length > 0) {
        setCompanies(cachedData);
      } else {
        setCompanies([]);
      }
      setLoadedShopId(shopIdFromSources);
      setCurrentShopId(parseInt(shopIdFromSources));
      setPage(1); // ⭐ reset page on shop change
    }
  }, [location.pathname]);

  useEffect(() => {
    const handleShopChanged = (event) => {
      if (event.detail?.shopId) {
        const newShopId = event.detail.shopId.toString();
        const cachedData = getCachedCompanies(newShopId);
        if (cachedData) setCompanies(cachedData);
        else setCompanies([]);
        setLoadedShopId(newShopId);
        setCurrentShopId(parseInt(newShopId));
        setPage(1); // ⭐ reset page on shop change
      }
    };
    window.addEventListener('shopChanged', handleShopChanged);
    return () => window.removeEventListener('shopChanged', handleShopChanged);
  }, []);

  useEffect(() => {
    if (currentShopId) fetchCompanies(currentShopId);
  }, [currentShopId]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPage(1); // ⭐ search change → page 1
  };

  // ===== Filtering =====
  const filteredCompanies = companies.filter(company =>
    company.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ===== ⭐ Pagination (exactly like ProductListUK) =====
  const pages = Math.max(1, Math.ceil(filteredCompanies.length / PAGE_SIZE));
  const current = filteredCompanies.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ⭐ Filter / search se pages kam ho jayein to page number bahar na reh jaye
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);

  // All shops except the current one
  const otherShops = shops.filter(s => String(s.shop_id) !== String(currentShopId));

  const resetMoveState = () => {
    setShowMoveList(false);
    setMoveShopIds([]);
  };

  // ⭐ Select All → only current page rows
  const handleSelectAll = () => {
    if (current.length === 0) return;
    const allSelected = current.every(c => selectedCompanies.includes(c.company_id));
    if (allSelected) {
      setSelectedCompanies(selectedCompanies.filter(id => !current.some(c => c.company_id === id)));
    } else {
      setSelectedCompanies([...new Set([...selectedCompanies, ...current.map(c => c.company_id)])]);
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleSelectCompany = (companyId) => {
    if (selectedCompanies.includes(companyId)) {
      setSelectedCompanies(selectedCompanies.filter(id => id !== companyId));
    } else {
      setSelectedCompanies([...selectedCompanies, companyId]);
    }
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleToggleMoveShop = (id) => {
    const key = String(id);
    if (moveShopIds.includes(key)) {
      setMoveShopIds(moveShopIds.filter(x => x !== key));
    } else {
      setMoveShopIds([...moveShopIds, key]);
    }
  };

  const handleMoveSelected = async () => {
    if (selectedCompanies.length === 0) {
      showWarningAlert('Please select companies first');
      return;
    }
    if (moveShopIds.length === 0) {
      showWarningAlert('Please select at least one shop');
      return;
    }

    setMoveLoading(true);

    const companiesToCopy = companies.filter(c => selectedCompanies.includes(c.company_id));
    let successCount = 0;
    let failedCount = 0;

    try {
      for (const targetId of moveShopIds) {
        for (const company of companiesToCopy) {
          try {
            const response = await fetch(`${API_BASE_URL}/implant-companies/create/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                company_name: company.company_name,
                shop_id: parseInt(targetId)
              })
            });
            if (response.ok) successCount++;
            else failedCount++;
          } catch (err) {
            failedCount++;
          }
        }
      }

      if (successCount > 0 && failedCount === 0) {
        showSuccessAlert(`${successCount} company/companies copied successfully!`);
      } else if (successCount > 0) {
        showWarningAlert(`${successCount} copied, ${failedCount} failed (maybe already exist).`);
      } else {
        showErrorAlert('Could not copy companies (maybe they already exist in those shops).');
      }

      setSelectedCompanies([]);
      setShowActionDropdown(false);
      resetMoveState();
      clearCompaniesCache(currentShopId);
      await fetchCompanies(currentShopId);
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDeleteSingleCompany = async (company) => {
    const result = await showDeleteConfirmation(company.company_name);
    if (result.isConfirmed) {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/implant-companies/${company.company_id}/delete/`, { method: 'DELETE' });
        if (response.ok || response.status === 204) {
          showSuccessAlert('Company deleted successfully!');
          setSelectedCompanies(selectedCompanies.filter(id => id !== company.company_id));
          clearCompaniesCache(currentShopId);
          await fetchCompanies(currentShopId);
        } else {
          showErrorAlert('Failed to delete company');
        }
      } catch (error) {
        showErrorAlert('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedCompanies.length === 0) {
      showWarningAlert('Please select companies to delete');
      return;
    }
    const result = await showDeleteConfirmation(null, true, selectedCompanies.length);
    if (result.isConfirmed) {
      setLoading(true);
      try {
        let deletedCount = 0;
        for (const companyId of selectedCompanies) {
          const response = await fetch(`${API_BASE_URL}/implant-companies/${companyId}/delete/`, { method: 'DELETE' });
          if (response.ok || response.status === 204) deletedCount++;
        }
        showSuccessAlert(`${deletedCount} company/companies deleted successfully!`);
        setSelectedCompanies([]);
        setShowActionDropdown(false);
        clearCompaniesCache(currentShopId);
        await fetchCompanies(currentShopId);
      } catch (error) {
        showErrorAlert('Failed to delete some companies. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleClearAll = () => {
    setSelectedCompanies([]);
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleOpenModalForNew = () => {
    setEditingCompany(null);
    setCompanyName('');
    setSelectedShopId('');
    setIsModalOpen(true);
  };

  const handleOpenModalForEdit = (company) => {
    setEditingCompany(company);
    setCompanyName(company.company_name);
    setSelectedShopId(company.shop?.shop_id?.toString() || '');
    setIsModalOpen(true);
  };

  const handleCompanyNameChange = (e) => setCompanyName(e.target.value);

  const handleShopChange = async (e) => {
    const shopId = e.target.value;
    setSelectedShopId(shopId);
  };

  const handleSave = async () => {
    if (!companyName.trim()) {
      showWarningAlert('Please enter Company Name');
      return;
    }
    const targetShopId = editingCompany ? editingCompany.shop?.shop_id : currentShopId;
    if (!targetShopId) {
      showWarningAlert('No shop selected');
      return;
    }
    setSaveLoading(true);
    try {
      const requestData = { company_name: companyName.trim(), shop_id: targetShopId };

      if (editingCompany) {
        const response = await fetch(`${API_BASE_URL}/implant-companies/${editingCompany.company_id}/update/`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData)
        });
        const data = await response.json();
        if (response.ok) {
          showSuccessAlert('Company updated successfully!');
          setIsModalOpen(false);
          setEditingCompany(null);
          setCompanyName('');
          setSelectedShopId('');
          clearCompaniesCache(currentShopId);
          await fetchCompanies(currentShopId);
        } else {
          showErrorAlert(data.error || 'Failed to update company');
        }
      } else {
        const response = await fetch(`${API_BASE_URL}/implant-companies/create/`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(requestData)
        });
        const data = await response.json();
        if (response.ok) {
          showSuccessAlert('Company added successfully!');
          setIsModalOpen(false);
          setCompanyName('');
          setSelectedShopId('');
          clearCompaniesCache(currentShopId);
          await fetchCompanies(currentShopId);
        } else {
          showErrorAlert(data.error || 'Failed to add company');
        }
      }
    } catch (error) {
      showErrorAlert('Network error. Please check if backend server is running.');
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDelete = async () => {
    if (editingCompany) {
      const result = await showDeleteConfirmation(editingCompany.company_name);
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const response = await fetch(`${API_BASE_URL}/implant-companies/${editingCompany.company_id}/delete/`, { method: 'DELETE' });
          if (response.ok || response.status === 204) {
            showSuccessAlert('Company deleted successfully!');
            setIsModalOpen(false);
            setEditingCompany(null);
            setSelectedCompanies(selectedCompanies.filter(id => id !== editingCompany.company_id));
            clearCompaniesCache(currentShopId);
            await fetchCompanies(currentShopId);
          } else {
            showErrorAlert('Failed to delete company');
          }
        } catch (error) {
          showErrorAlert('Network error. Please try again.');
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCompany(null);
    setCompanyName('');
    setSelectedShopId('');
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
      resetMoveState();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  const getShopDisplayName = (company) => {
    if (company.shop) return `${company.shop.shop_name} (ID: ${company.shop.shop_id})`;
    return 'No Shop';
  };

  const isOperationInProgress = loading || saveLoading || moveLoading;

  const shopSelectValue = selectedShopId
    ? String(selectedShopId)
    : (currentShopId ? String(currentShopId) : '');

  return (
    <div className="implant-companies-main-container">

      {/* Heading + Search + Buttons */}
      <div className="implant-companies-action-section">
        <div className="implant-companies-heading">
          <h2>
            <FaChartSimple className="implant-companies-heading-icon" />
            Implant Companies Management
          </h2>
        </div>

        <div className="implant-companies-action-right">
          <div className="implant-companies-search-wrapper">
            <FaSearch className="implant-companies-search-icon" />
            <input
              type="text"
              placeholder="Search by Company Name..."
              value={searchTerm}
              onChange={handleSearch}
              className="implant-companies-search-input"
              disabled={isOperationInProgress}
            />
          </div>

          <div className="implant-companies-action-buttons">
            <button
              className="implant-companies-select-all-btn"
              onClick={handleSelectAll}
              disabled={isOperationInProgress}
            >
              <FaCheckDouble />
              <span>{current.length > 0 && current.every(c => selectedCompanies.includes(c.company_id)) ? 'Deselect All' : 'Select All'}</span>
            </button>
            <button
              className="implant-companies-add-btn"
              onClick={handleOpenModalForNew}
              disabled={isOperationInProgress}
            >
              <FaPlus />
              <span>ADD COMPANY</span>
            </button>
          </div>
        </div>
      </div>

      {selectedCompanies.length > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedCompanies.length} company/companies selected
            </span>
            <div className="action-dropdown-container">
              <button
                className="action-dropdown-btn"
                onClick={() => {
                  setShowActionDropdown(!showActionDropdown);
                  resetMoveState();
                }}
                disabled={isOperationInProgress}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => setShowMoveList(!showMoveList)}
                    disabled={isOperationInProgress}
                  >
                    <FaChevronRight className={`move-arrow ${showMoveList ? 'move-arrow-open' : ''}`} />
                    <span>Move To</span>
                  </button>

                  {showMoveList && (
                    <div className="move-shops-panel">
                      <div className="move-shops-list">
                        {otherShops.length > 0 ? (
                          otherShops.map(shop => (
                            <label key={shop.shop_id} className="move-shop-item">
                              <input
                                type="checkbox"
                                checked={moveShopIds.includes(String(shop.shop_id))}
                                onChange={() => handleToggleMoveShop(shop.shop_id)}
                                disabled={moveLoading}
                              />
                              <span>{shop.shop_name}</span>
                            </label>
                          ))
                        ) : (
                          <div className="move-no-shops">No other shops</div>
                        )}
                      </div>
                      {otherShops.length > 0 && (
                        <button
                          className="move-apply-btn"
                          onClick={handleMoveSelected}
                          disabled={moveLoading || moveShopIds.length === 0}
                        >
                          {moveLoading ? 'Copying...' : `Copy to ${moveShopIds.length} shop(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                    disabled={isOperationInProgress}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedCompanies.length})</span>
                  </button>
                </div>
              )}
            </div>
            <button className="clear-selection" onClick={handleClearAll} disabled={isOperationInProgress}>
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="implant-companies-table-container">
        <div className="implant-companies-table-wrapper">
          <table className="implant-companies-table">
            <thead>
              <tr>
                <th className="implant-companies-checkbox-cell">
                  <input
                    type="checkbox"
                    checked={current.length > 0 && current.every(c => selectedCompanies.includes(c.company_id))}
                    onChange={handleSelectAll}
                    disabled={isOperationInProgress}
                  />
                </th>
                <th>COMPANY NAME</th>
                <th>SHOP</th>
                <th className="implant-companies-actions-cell">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {current.length > 0 ? (
                current.map((company) => (
                  <tr
                    key={company.company_id}
                    className={selectedCompanies.includes(company.company_id) ? 'implant-companies-selected-row' : ''}
                  >
                    <td className="implant-companies-checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedCompanies.includes(company.company_id)}
                        onChange={() => handleSelectCompany(company.company_id)}
                        disabled={isOperationInProgress}
                      />
                    </td>
                    <td className="implant-companies-name-cell">
                      <span className="implant-companies-name">{company.company_name}</span>
                    </td>
                    <td className="implant-companies-shop-cell">
                      <span className="implant-companies-shop-badge">{getShopDisplayName(company)}</span>
                    </td>
                    <td className="implant-companies-actions-cell">
                      <button
                        className="implant-companies-edit-btn"
                        onClick={() => handleOpenModalForEdit(company)}
                        title="Edit Company"
                        disabled={isOperationInProgress}
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="implant-companies-delete-btn"
                        onClick={() => handleDeleteSingleCompany(company)}
                        title="Delete Company"
                        disabled={isOperationInProgress}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="implant-companies-no-data-row">
                    <div className="implant-companies-no-data">
                      <FaBuilding className="implant-companies-no-data-icon" />
                      <p>No implant companies found for {shopName || 'this shop'} (ID: {currentShopId}).</p>
                      <button
                        className="implant-companies-add-first-btn"
                        onClick={handleOpenModalForNew}
                        disabled={isOperationInProgress}
                      >
                        <FaPlus /> Add Your First Company
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ✅ 10 per page: 1-10 of N | < Previous | Page 1 of N | Next > */}
      <TablePagination
        page={page}
        totalItems={filteredCompanies.length}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Modal */}
      {isModalOpen && (
        <div className="implant-companies-modal-overlay" onClick={handleCloseModal}>
          <div className="implant-companies-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="implant-companies-modal-header">
              <h3>{editingCompany ? 'Edit Company' : 'Add New Company'}</h3>
              <button className="implant-companies-modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="implant-companies-modal-body">
              <form className="implant-companies-form" onSubmit={(e) => e.preventDefault()}>

                <div className="implant-companies-form-row">
                  <label className="implant-companies-form-label">SHOP:</label>
                  <select
                    className="implant-companies-shop-select-dropdown"
                    value={shopSelectValue}
                    onChange={handleShopChange}
                    disabled={isOperationInProgress || !!editingCompany}
                  >
                    <option value="">-- Select a shop --</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={String(shop.shop_id)}>
                        {shop.shop_name} (ID: {shop.shop_id})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="implant-companies-form-row">
                  <label className="implant-companies-form-label">COMPANY NAME:</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={handleCompanyNameChange}
                    placeholder="Enter company name"
                    autoFocus
                    className="implant-companies-input"
                    disabled={isOperationInProgress}
                  />
                </div>

              </form>
            </div>
            <div className="implant-companies-modal-footer">
              <button className="implant-companies-btn-cancel" onClick={handleCloseModal} disabled={isOperationInProgress}>
                Cancel
              </button>
              {editingCompany && (
                <button className="implant-companies-btn-delete" onClick={handleDelete} disabled={isOperationInProgress}>
                  Delete
                </button>
              )}
              <button className="implant-companies-btn-save" onClick={handleSave} disabled={isOperationInProgress}>
                {isOperationInProgress ? 'Saving...' : (editingCompany ? 'Update Company' : 'Save Company')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImplantCompanies;